<template>
  <div class="wl-list wl-form">
    <div class="wl-list__header">
      <span class="wl-list__title">
        {{ isWaypoint ? '航点列表' : '区域顶点' }}
        <a-popover placement="rightTop" overlay-class-name="wl-popover">
          <template #content>
            <div class="wl-help">
              <h4>{{ isWaypoint ? '航点列表操作说明' : '区域顶点操作说明' }}</h4>
              <table>
                <thead><tr><th>操作</th><th>功能</th></tr></thead>
                <tbody v-if="isWaypoint">
                  <tr><td>Shift + 点击【航点】</td><td>多选航点批量编辑</td></tr>
                  <tr><td>点击【航点】</td><td>选中航点，虚拟飞行器移动到该航点</td></tr>
                  <tr><td>双击【航点】</td><td>视角定位到该航点</td></tr>
                  <tr><td>右键【航点】</td><td>复制/应用动作模板、设置禁止返航点、删除航点</td></tr>
                  <tr><td>拖动【航点】</td><td>调整航点排序</td></tr>
                  <tr><td>◀ / ▶</td><td>上一航点 / 下一航点</td></tr>
                  <tr><td>▲ / ▼</td><td>上一动作 / 下一动作</td></tr>
                  <tr><td>Delete</td><td>删除选中航点</td></tr>
                </tbody>
                <tbody v-else>
                  <tr><td>点击【顶点】</td><td>选中顶点，新顶点插入到其后</td></tr>
                  <tr><td>地图拖动【顶点】</td><td>调整区域边界</td></tr>
                  <tr><td>Delete</td><td>删除选中顶点</td></tr>
                </tbody>
              </table>
            </div>
          </template>
          <InfoCircleOutlined class="wl-list__help" />
        </a-popover>
      </span>
      <span class="wl-list__tools">
        <a-tooltip v-if="isWaypoint" title="反转航点顺序，成对的开始/结束动作自动交换"><button type="button" :disabled="draft.waypoint.waypoints.length < 2" @click="editor.reverseRoute"><SwapOutlined /></button></a-tooltip>
        <a-tooltip title="撤销 (Ctrl+Z)"><button type="button" :disabled="!editor.canUndo.value" @click="editor.undo"><UndoOutlined /></button></a-tooltip>
      </span>
    </div>

    <div class="wl-list__stats">
      <div title="航线长度"><ColumnWidthOutlined /><b>{{ formatDistance(stats.distance) }}</b></div>
      <div title="预计时长"><ClockCircleOutlined /><b>{{ formatDuration(stats.duration) }}</b></div>
      <div :title="isWaypoint ? '航点数' : '航点数（生成）'"><span class="wl-tri" /><b>{{ stats.waypointCount }}</b></div>
      <div title="照片数"><CameraOutlined /><b>{{ stats.photoCount }}</b></div>
      <div v-if="!isWaypoint" title="面积"><BorderOutlined /><b>{{ formatArea(stats.area) }}</b></div>
    </div>

    <div class="wl-list__body uranus-scrollbar">
      <template v-if="isWaypoint">
        <p v-if="!draft.waypoint.waypoints.length" class="wl-list__empty">
          {{ editor.aircraft.active ? '用 W/A/S/D 飞行、C/Z 升降、Q/E 转向，按空格在虚拟飞行器位置新增航点。' : '设置参考起飞点后点击“模拟起飞”，或选择“点击地图添加”。' }}
        </p>
        <a-dropdown v-for="(waypoint, index) in draft.waypoint.waypoints" :key="waypoint.id" :trigger="['contextmenu']" overlay-class-name="wl-menu">
          <div
            class="wl-row"
            :class="{ selected: editor.selectedIds.value.includes(waypoint.id), warn: lowIndexes.has(index), risky: waypoint.isRisky, dragging: dragIndex === index, over: overIndex === index }"
            draggable="true"
            @click="editor.selectWaypoint(waypoint.id, $event.shiftKey)"
            @dblclick="editor.flyToSelected()"
            @dragstart="onDragStart(index, $event)"
            @dragover.prevent="overIndex = index"
            @dragleave="overIndex === index && (overIndex = -1)"
            @drop.prevent="onDrop(index)"
            @dragend="dragIndex = -1; overIndex = -1"
          >
            <span class="wl-row__index"><span class="wl-tri" :class="{ warn: lowIndexes.has(index), risky: waypoint.isRisky }" />{{ index + 1 }}</span>
            <span class="wl-row__actions">
              <span v-for="action in waypoint.actions.slice(0, 5)" :key="action.id" class="wl-row__action" :title="ACTION_LABELS[action.type]"><component :is="ACTION_ICONS[action.type]" /></span>
              <span v-if="waypoint.actions.length > 5" class="wl-row__action wl-row__action--more">+{{ waypoint.actions.length - 5 }}</span>
            </span>
            <span class="wl-row__height">{{ waypointHeight(draft, waypoint).toFixed(0) }} m</span>
          </div>
          <template #overlay>
            <a-menu>
              <a-menu-item @click="editor.copyActions(waypoint.id)">复制动作为模板</a-menu-item>
              <a-menu-item :disabled="!editor.hasActionTemplate.value" @click="editor.applyActionTemplate(waypoint.id)">应用动作模板</a-menu-item>
              <a-menu-item v-if="editor.droneProfile.value?.supportsRisky" @click="editor.toggleRisky(waypoint.id)">{{ waypoint.isRisky ? '取消禁止返航点' : '设置禁止返航点' }}</a-menu-item>
              <a-menu-item danger @click="editor.removeWaypoint(waypoint.id)">删除航点</a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </template>
      <template v-else>
        <p v-if="!draft.mapping.vertices.length" class="wl-list__empty">在地图上依次点击绘制测绘区域（至少 3 个顶点）。</p>
        <div
          v-for="(vertex, index) in draft.mapping.vertices"
          :key="vertex.id"
          class="wl-row"
          :class="{ selected: editor.selectedIds.value.includes(vertex.id) }"
          @click="editor.selectedIds.value = [vertex.id]"
        >
          <span class="wl-row__index"><span class="wl-tri area" />{{ index + 1 }}</span>
          <span class="wl-row__coords">{{ vertex.latitude.toFixed(5) }}, {{ vertex.longitude.toFixed(5) }}</span>
          <button type="button" class="wl-row__delete" title="删除顶点" @click.stop="editor.removeVertex(vertex.id)"><DeleteOutlined /></button>
        </div>
        <p v-if="generated" class="wl-list__generated">航线间距 {{ generated.lineSpacing }} m · 拍照间隔 {{ draft.mapping.shootType === 'time' ? generated.triggerParam + ' s' : generated.triggerParam + ' m' }} · 生成 {{ generated.waypoints.length }} 个航点</p>
      </template>
    </div>

    <div v-if="errors.length" class="wl-list__issues">
      <div v-for="(issue, index) in errors.slice(0, 3)" :key="index" class="error" @click="issue.index !== undefined && editor.selectByOffset(0)"><ExclamationCircleFilled /> {{ issue.message }}</div>
    </div>
    <div class="wl-list__footer">
      <a-button type="primary" block :loading="editor.saving.value" :disabled="hasErrors(editor.issues.value)" @click="editor.save">保存到航线库</a-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { Component } from 'vue'
import {
  AimOutlined, BorderOutlined, CameraOutlined, ClockCircleOutlined, ColumnWidthOutlined, CompassOutlined, DeleteOutlined, ExclamationCircleFilled, FieldTimeOutlined,
  FolderOutlined, GlobalOutlined, InfoCircleOutlined, PauseCircleOutlined, PictureOutlined, RotateRightOutlined, StopOutlined, SwapOutlined, UndoOutlined,
  VideoCameraAddOutlined, VideoCameraOutlined, ZoomInOutlined,
} from '@ant-design/icons-vue'
import type { WaypointActionType } from '/@/types/wayline-editor'
import { ACTION_LABELS, formatArea, formatDistance, formatDuration, generateMappingRoute, hasErrors, waypointHeight } from '/@/utils/wayline-editor'
import { useWaylineEditor } from './editor-context'

const editor = useWaylineEditor()
const draft = editor.draft
const stats = editor.stats
const isWaypoint = computed(() => draft.templateType === 'waypoint')
const generated = computed(() => draft.templateType === 'mapping2d' ? generateMappingRoute(draft) : null)
const lowIndexes = computed(() => new Set(editor.nearGround.value.map(issue => issue.index)))
const errors = computed(() => editor.issues.value.filter(issue => issue.level === 'error'))
const dragIndex = ref(-1)
const overIndex = ref(-1)

const ACTION_ICONS: Record<WaypointActionType, Component> = {
  takePhoto: CameraOutlined,
  startRecord: VideoCameraAddOutlined,
  stopRecord: VideoCameraOutlined,
  hover: PauseCircleOutlined,
  gimbalRotate: CompassOutlined,
  rotateYaw: RotateRightOutlined,
  zoom: ZoomInOutlined,
  focus: AimOutlined,
  customDirName: FolderOutlined,
  panoShot: GlobalOutlined,
  orientedShoot: PictureOutlined,
  startTimedShot: FieldTimeOutlined,
  startDistanceShot: ColumnWidthOutlined,
  endIntervalShot: StopOutlined,
}

function onDragStart (index: number, event: DragEvent) {
  dragIndex.value = index
  event.dataTransfer?.setData('text/plain', String(index))
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

function onDrop (index: number) {
  if (dragIndex.value >= 0) editor.reorderWaypoints(dragIndex.value, index)
  dragIndex.value = -1
  overIndex.value = -1
}
</script>

<style lang="scss">
.wl-list {
  display: flex; flex-direction: column; height: 100%; min-height: 0;
  &__header { display: flex; align-items: center; justify-content: space-between; height: 40px; padding: 0 8px 0 12px; border-bottom: 1px solid var(--wl-border); }
  &__title { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #fff; }
  &__help { color: var(--wl-muted); cursor: help; font-size: 13px; }
  &__help:hover { color: #fff; }
  &__tools { display: flex; gap: 2px; }
  &__tools button { width: 26px; height: 26px; border: 0; border-radius: 4px; background: transparent; color: #fff; cursor: pointer; font-size: 14px; }
  &__tools button:hover:not(:disabled) { background: #ffffff1a; }
  &__tools button:disabled { color: #5c6068; cursor: not-allowed; }
  &__stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px; padding: 8px 8px; border-bottom: 1px solid var(--wl-border); }
  &__stats div { display: flex; flex-direction: column; align-items: center; gap: 2px; color: var(--wl-muted); font-size: 13px; }
  &__stats b { color: #fff; font-weight: 500; font-size: 12px; white-space: nowrap; }
  &__body { flex: 1; min-height: 0; overflow: auto; padding: 4px 0; }
  &__empty { margin: 0; padding: 18px 14px; color: var(--wl-muted); line-height: 1.7; text-align: center; }
  &__generated { margin: 6px 12px; color: var(--wl-muted); }
  &__issues { max-height: 96px; overflow: auto; padding: 4px 12px; border-top: 1px solid var(--wl-border); }
  &__issues .error { color: #ffb4a9; padding: 2px 0; cursor: pointer; }
  &__footer { padding: 8px 10px; border-top: 1px solid var(--wl-border); }
}
.wl-tri { display: inline-block; width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-top: 10px solid var(--wl-accent); }
.wl-tri.warn { border-top-color: var(--wl-warn); }
.wl-tri.risky { border-top-color: #ff6b6b; }
.wl-tri.area { border-top-color: #4ea1ff; }
.wl-row {
  display: flex; align-items: center; gap: 8px; height: 30px; padding: 0 10px; border-left: 3px solid transparent; cursor: pointer; user-select: none;
  &:hover { background: #ffffff0d; }
  &.selected { background: #2d8cf01f; border-left-color: var(--wl-accent); }
  &.warn.selected { border-left-color: var(--wl-warn); }
  &.dragging { opacity: 0.4; }
  &.over { box-shadow: inset 0 2px 0 var(--wl-accent); }
  &__index { display: inline-flex; align-items: center; gap: 5px; min-width: 34px; color: #fff; font-weight: 600; }
  &__actions { display: flex; flex: 1; gap: 4px; min-width: 0; overflow: hidden; }
  &__action { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 20px; border-radius: 3px; background: #2c2d31; color: #d5d8de; font-size: 12px; flex-shrink: 0; }
  &__action--more { font-size: 10px; color: var(--wl-muted); }
  &__height { color: var(--wl-muted); font-variant-numeric: tabular-nums; }
  &__coords { flex: 1; color: #d5d8de; font-variant-numeric: tabular-nums; }
  &__delete { border: 0; background: transparent; color: var(--wl-muted); cursor: pointer; }
  &__delete:hover { color: var(--wl-danger); }
}
.wl-help { min-width: 360px; }
.wl-help h4 { margin: 0 0 8px; color: #fff; font-size: 13px; }
.wl-help table { width: 100%; border-collapse: collapse; }
.wl-help th, .wl-help td { padding: 6px 8px; border-bottom: 1px solid #35373c; text-align: left; vertical-align: top; }
.wl-help th { color: var(--wl-muted); font-weight: 500; }
.wl-help td:first-child { white-space: nowrap; color: #fff; }
</style>
